import { inject, Injectable } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LoaderService } from '../../core/services/loader/loader.service';
import { CommonHttpsService } from '../../core/services/https/common-https.service';
import { ToasterMessageService } from '../../core/services/topbar-toaster/toaster-message.service';
import { headers } from '../../core/common/headers';
import { apiUrls } from '../../core/common/api_urls/api_urls';
import { CookieService } from 'ngx-cookie-service';
import { session } from '../../core/common/session/session';
import { environment } from '../../../environments/environment';
import { errorMessage } from '../../core/common/language/error';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  public errorMessage = errorMessage;
  constructor(
    public loadingService: LoaderService,
    public commonHttpService: CommonHttpsService,
    public messageService: ToasterMessageService,
    private http: HttpClient,
  ) {}

  cookiesService = inject(CookieService);

  header = new HttpHeaders({
    [headers.onboardingHeader]: 'true',
  });

  private normalizeArrayField(value: any) {
    return Array.isArray(value) && value.length > 0
      ? value.map((item: any) => item.id)
      : null;
  }

  private formatAsDateOnly(value: any): string | null {
    if (!value) return null;
    // Accept Date object or string; produce YYYY-MM-DD
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  saveFilters(data: any, screenId: any) {
    const bodyData = {
      practice_id: data?.practice ? data?.practice : null,
      clinic_ids: this.normalizeArrayField(data?.clinic),
      program_ids: this.normalizeArrayField(data?.program),
      registration_type: this.normalizeArrayField(data?.registrationType),
      status: this.normalizeArrayField(data?.patientStatus),
      from_timestamp: this.formatAsDateOnly(data?.dateFrom),
      to_timestamp: this.formatAsDateOnly(data?.dateTo),
      screen_id: screenId,
    };

    return this.commonHttpService
      .AddEntity(apiUrls.filters.insertFilter, bodyData, {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  applyFilters(data: any, dashboard_type: string) {
    const bodyData = {
      practice_id: data?.practice ? data?.practice : null,
      clinic_ids: this.normalizeArrayField(data?.clinic),
      program_ids: this.normalizeArrayField(data?.program),
      registration_type: this.normalizeArrayField(data?.registrationType),
      status: this.normalizeArrayField(data?.patientStatus),
      from_timestamp: this.formatAsDateOnly(data?.dateFrom),
      to_timestamp: this.formatAsDateOnly(data?.dateTo),
    };

    return this.commonHttpService
      .AddEntity(
        apiUrls.filters.applyFilter(dashboard_type),
        bodyData,
        { headers: this.header },
        false,
      )
      .pipe(
        tap(() => this.loadingService.stopSpinner()),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getFilters(screenId: any, dashboard_type: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.filters.getFilterByScreenId(screenId, dashboard_type),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );

          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  exportToExcel(data: any, dashboard_type: string) {
    const bodyData = {
      practice_id: data?.practice ? data?.practice : null,
      clinic_ids: data?.clinic
        ? data?.clinic.map((item: any) => item.id)
        : null,
      program_ids: data?.program
        ? data?.program.map((item: any) => item.id)
        : null,
      registration_type: data?.registrationType
        ? data?.registrationType.map((item: any) => item.id)
        : null,
      status: data?.patientStatus
        ? data?.patientStatus.map((item: any) => item.id)
        : null,
      from_timestamp: this.formatAsDateOnly(data?.dateFrom),
      to_timestamp: this.formatAsDateOnly(data?.dateTo),
    };

    const _headers = new HttpHeaders({
      [headers.onboardingHeader]: 'true',
      'Content-Type': 'application/json',
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    return this.http
      .post(
        `${environment.onboardingUrl}/api/${apiUrls.filters.exportToExcel(dashboard_type)}`,
        bodyData,
        { headers: _headers, responseType: 'blob' },
      )
      .pipe(
        tap((data: any) => {}),
        catchError((error) => {
          console.error('Error while Adding etntiy:', error);
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );

          return throwError(() => error);
        }),
      );
  }

  getToken() {
    // localStorage.setItem('deviceRegistered', 'false');
    const identity = 'noreply@caregenix.solutions';
    const detail = '1234567890/1/1/2/onboarding';

    // return this.commonHttpService
    //   .AddEntity(
    //     apiUrls.twilioGenerateToken.generateToken,
    //     { detail: detail, identity: identity },
    //     {
    //       headers: this.header,
    //     },
    //   )
    //   .pipe(
    //     tap((data) => {}),
    //     catchError((error: any) => {
    //       this.messageService.errroMessage(error?.message || error, true);
    //       this.loadingService.stopSpinner();
    //       return throwError(() => error);
    //     }),
    //   );

    let authToken = this.cookiesService.get(session.authToken);
    let user;
    const userSettingsString = localStorage.getItem('userSettings');
    if (userSettingsString) {
      const userSettings = JSON.parse(userSettingsString);
      user = userSettings?.userInfo || {};
    }

    let HEADER;

    if (authToken) {
      HEADER = {
        'X-Token': authToken,
        'X-Username': user?.username,
      };
    }

    return this.http
      .post<{
        token: string;
        cognitoUserId: string;
      }>(
        `${environment.twilioUrl}/api/generate-token`,
        { detail: detail },
        {
          headers: HEADER,
        },
      )
      .pipe(
        tap((data) => {
          localStorage.setItem('cognitoID', data.cognitoUserId);
        }),
        catchError((error: any) => {
          localStorage.setItem('deviceRegistered', 'false');
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  GetConferenceId(patientId: any) {
    let authToken = this.cookiesService.get(session.authToken);
    let user;
    const userSettingsString = localStorage.getItem('userSettings');
    if (userSettingsString) {
      const userSettings = JSON.parse(userSettingsString);
      user = userSettings?.userInfo || {};
    }

    let HEADER;

    if (authToken) {
      HEADER = {
        'X-Token': authToken,
        'X-Username': user?.username,
      };
    }

    return this.http
      .get<{
        token: string;
      }>(
        `${environment.twilioUrl}/api/GetConferenceId?patientId=${patientId}`,
        {
          headers: HEADER,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );

          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }
}
